import _ from 'lodash';
import analytics from '@nerdwallet/analytics';
import num from '@nerdwallet/nw-client-lib/num';

import AmortizationInputLibrary from '../../components/amortization-calculator/input-library';
import HomeAffordabilityInputLibrary from '../../components/home-affordability-calculator/input-library';
import MortgageInputLibrary from '../../components/mortgage-calculator/shared/input-library';
import RefinanceInputLibrary from '../../components/refinance-calculator/input-library';
import RentVsBuyInputLibrary from '../../components/rentvsbuy-calculator/control-panel/shared/input-library';
import CostOfLivingInputLibrary from '../../components/cost-of-living-calculator/compare-cities/input-library';
import {
  inputChange,
  overridesChange,
  saveHomeCalculatorProfile,
} from '../actions';
import { cityChange } from '../col/col-actions';
import saveToProfile from '../actions/save-to-profile';
import {
  HOME_AFFORDABILITY,
  REFINANCE,
  MORTGAGE,
  AMORTIZATION,
  RENT_VS_BUY,
  CALCULATOR_REDUCER_NAME_HASH,
  COST_OF_LIVING,
} from '../analytics-constants';
import type { Calculator, CalculatorReducerName } from '../analytics-constants';

import type { Field } from './inputs';
import {
  ValidationObject,
  validateField,
  ValidationLibrary,
} from './validations';

const CURRENCY = 'currency';
const NUMBER = 'number';
const PERCENTAGE = 'percentage';

type InputType = 'currency' | 'percentage' | 'string' | 'number';
type OnChangeEvent = React.ChangeEvent<HTMLInputElement>;

/**
 * NumStepper returns a number instead of an expected OnChangeEvent
 */
type Event = OnChangeEvent | number;

type Value = string | number | boolean;

const getInputLibrary = (calculator: Calculator): ValidationLibrary =>
  ({
    [HOME_AFFORDABILITY]: HomeAffordabilityInputLibrary,
    [REFINANCE]: RefinanceInputLibrary,
    [MORTGAGE]: MortgageInputLibrary,
    [AMORTIZATION]: AmortizationInputLibrary,
    [RENT_VS_BUY]: RentVsBuyInputLibrary,
    [COST_OF_LIVING]: CostOfLivingInputLibrary,
  }[calculator]);

export const getCalculatorReducerName = (
  calculator: Calculator
): CalculatorReducerName => CALCULATOR_REDUCER_NAME_HASH[calculator];

export interface BedrockInterface
  extends Omit<any, 'value_i' | 'value_n' | 'value_s' | 'value_ts'> {
  value?: Value;
  valueFieldName: 'value_i' | 'value_n' | 'value_s' | 'value_ts';
  /**
   * There're some cases were analytics is expecting a different value rather than the one from the input onChange event
   * (e.g: RefinanceGoalInput & CreditScoreInput)
   */
  mapValue?: (value: Value) => string | number | null;
}

type TrackOnChange = {
  value: string | number | boolean;
  bedrock?: BedrockInterface | Record<string, any>;
  validation: ValidationObject;
};

const trackInputChange = ({
  bedrock = {},
  validation,
  value,
}: TrackOnChange) => {
  // eventType is a required property on bedrock
  const bedrockAnalytics = !!bedrock?.eventType;
  if (bedrockAnalytics) {
    const { valid, message } = validation;
    const { valueFieldName, eventType, mapValue, ...otherProps } = bedrock;
    const analyticsValue = mapValue ? mapValue(value) : value;
    const bedrockPayload = {
      ...otherProps,
      is_valid: valid,
    };

    if (!valid) {
      // @ts-ignore - this property is expected
      bedrockPayload.invalid_field_value = String(analyticsValue);
      // @ts-ignore - this property is expected
      bedrockPayload.validation_failure_message = message;
    } else {
      const trackValue =
        valueFieldName === 'value_s' ? String(analyticsValue) : analyticsValue;
      bedrockPayload[valueFieldName] = trackValue;
    }
    analytics.track(eventType, bedrockPayload);
  }
};

export const debouncedTrackOnChange = _.debounce(trackInputChange, 1000);

const NO_DIGITS_REGEX = new RegExp(/[^0-9]+/g);

const getValue = (target: string, inputType: InputType) => {
  switch (inputType) {
    case CURRENCY:
    case NUMBER:
    case PERCENTAGE:
      return num.value(target);
    default:
      return target;
  }
};

type OnChange = {
  fieldName: Field;
  /**
   * there are some scenarios that 1 input overrides another calculated value, that's why we need to know
   * which input we need to validate
   * (e.g: value for DownPayment (HA) is taken from `fundsNeeded` but we want to validate `downPayment)
   */
  overrides?: boolean;
  overridesValue?: string;
  event: Event;
  inputType?: InputType;
  bedrock?: BedrockInterface;
  dispatch: (p: any) => void;
  calculator: Calculator;
  validate?: boolean;
  additionalInputIdsToValidate?: Field[];
  analyticValue?: Value;
};

export const inputOnChange = ({
  fieldName,
  overrides,
  overridesValue,
  event,
  inputType = 'string',
  bedrock,
  dispatch,
  calculator,
  validate = true,
  additionalInputIdsToValidate,
  /* this is in case that the input value is not the expected value for analytics, so it ca be overriden */
  analyticValue = undefined,
}: OnChange): void => {
  const validationLibrary = getInputLibrary(calculator);
  const action = overrides ? overridesChange : inputChange;
  const targetValue =
    typeof event === 'number' ? String(event) : event?.target?.value ?? '';

  // if user types something different from digit, prevent changing value
  if (inputType === NUMBER && NO_DIGITS_REGEX.test(targetValue)) {
    return;
  }

  const value = getValue(targetValue, inputType);
  let validation: ValidationObject = { valid: true };

  if (validate) {
    validation = validateField(
      fieldName,
      value,
      validationLibrary,
      additionalInputIdsToValidate
    );
  }

  debouncedTrackOnChange({
    bedrock,
    validation,
    value: analyticValue || value,
  });

  const rootReducerName = getCalculatorReducerName(calculator);
  // if  overrides` is present, that means that to update `overrides` reducer, by default we change `manualInputs`
  let reducer = overrides ? 'overrides' : 'manualInputs';
  reducer = calculator !== 'costOfLiving' ? reducer : 'inputs';
  const valuePayload = overridesValue
    ? {
        [overridesValue]: {
          [fieldName]: value,
        },
      }
    : {
        [fieldName]: value,
      };
  const values = {
    name: rootReducerName,
    [reducer]: valuePayload,
  };

  if (calculator === 'costOfLiving') {
    dispatch(saveHomeCalculatorProfile(values.inputs));
    dispatch(saveToProfile());
    // @ts-ignore error
    dispatch(cityChange(values));
  }
  const allInputs = additionalInputIdsToValidate
    ? additionalInputIdsToValidate
        .map((input) => validationLibrary[input])
        .concat(validationLibrary[fieldName])
    : [validationLibrary[fieldName]];
  dispatch(action(values, _.compact(allInputs)));
};

export const inputOnBlur = ({
  fieldName,
  event,
  inputType = 'string',
  bedrock,
  calculator,
  validate = true,
  additionalInputIdsToValidate,
  /* this is in case that the input value is not the expected value for analytics, so it ca be overriden */
  analyticValue = undefined,
}: OnChange): void => {
  const validationLibrary = getInputLibrary(calculator);
  const targetValue =
    typeof event === 'number' ? String(event) : event?.target?.value ?? '';

  const value = getValue(targetValue, inputType);
  let validation: ValidationObject = { valid: true };

  if (validate) {
    validation = validateField(
      fieldName,
      value,
      validationLibrary,
      additionalInputIdsToValidate
    );
  }

  debouncedTrackOnChange({
    bedrock,
    validation,
    value: analyticValue || value,
  });
};
